import { yupResolver } from "@hookform/resolvers/yup";
import { useHeaderHeight } from "expo-router/react-navigation";
import { useRouter } from "expo-router";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import * as yup from "yup";

import CreateInvitationView, { IForm } from "./CreateInvitationView";

import {
  countryCallingCodes,
  spacingForDevicesWithoutNotch,
} from "@/config/constants";
import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import {
  InvitationsListMyOrganiationsSentInvitationsDocument,
  InvitationsListMyOrganiationsSentInvitationsQuery,
  InvitationsListMySentInvitationsDocument,
  InvitationStatus,
  Role,
  useInvitationsCreateInvitationMutation,
  useInvitationsListRolesQuery,
  User,
} from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useToastNotification } from "@/hooks/useToastNotification";
import { formatError } from "@/utils/formatError";
import { ifWeb } from "@/utils/platform";

/**
 * NativeWind `md:` breakpoint (768px). The content panel fixes its width at md+
 * and otherwise fills the viewport — a screen-width decision derived from the
 * measured window width rather than the vendored Gluestack breakpoint hook.
 */
const MD_BREAKPOINT = 768;

/**
 * Build a placeholder id for an optimistic entity.
 * @remarks
 * Extracted so the timestamp is actually testable (SE-6336). Both call sites
 * previously inlined `new Date().toISOString` WITHOUT the call parens, which
 * stringifies the function itself — every optimistic invitation got the
 * byte-identical id `temp-id-function toISOString() { [native code] }` and so
 * collided on a single normalized cache entry.
 * @param prefix - Distinguishes the invitation id from the receiver id.
 * @returns A prefixed ISO-8601 timestamp.
 */
export const makeOptimisticId = (prefix: string): string =>
  `${prefix}-${new Date().toISOString()}`;

const validCallingCodes = Object.values(countryCallingCodes).filter(
  code => code !== "N/A"
); // Remove non-applicable codes

const schema = yup.object().shape({
  callingCode: yup
    .string()
    .oneOf(validCallingCodes, "Please enter a valid country calling code")
    .required("Can't be blank"),
  phoneNumber: yup.string().required("Can't be blank"),
  roleName: yup
    .string()
    .oneOf(["MEMBER", "MANAGER", "OWNER"], "Must be MEMBER, MANAGER, or OWNER")
    .required("Can't be blank"),
});

/**
 * Container component for creating a new invitation.
 * Handles form validation and invitation submission with optimistic updates.
 */
const CreateInvitationContainer = () => {
  const currentUser = useCurrentUser();
  const heightHeader = useHeaderHeight();
  const toast = useToastNotification();
  const { top, bottom } = useSafeAreaInsets();
  const { data } = useInvitationsListRolesQuery();
  const roles = useMemo(
    () => data?.listRoles?.edges.map(edge => edge.node) ?? [],
    [data?.listRoles?.edges]
  );
  const recordUserGoal = useRecordUserGoal({
    goalType: GoalType.CreateInvitation,
    value: "",
  });
  const [createInvitation] = useInvitationsCreateInvitationMutation();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<IForm>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      callingCode: "1",
      phoneNumber: "",
      roleName: "MEMBER",
    },
  });

  const router = useRouter();

  const keyboardVerticalOffset =
    heightHeader +
    (top || spacingForDevicesWithoutNotch) +
    (bottom + spacingForDevicesWithoutNotch);

  const submit = useCallback(
    ({ phoneNumber, callingCode, roleName }: IForm) => {
      const fullPhoneNumber = `+${callingCode}${phoneNumber}`;
      const role = roles.find(
        r => r.name.toLowerCase() === roleName.toLowerCase()
      );
      return createInvitation({
        onCompleted: () => {
          recordUserGoal({
            value: fullPhoneNumber,
            goalType: GoalType.CreateInvitation,
          });
          // SE-4010: successful invite. Only the role is captured — the
          // invitee's phone number is personal data and stays out.
          void AnalyticsTracker.user.logUserInvited({
            invited_role: roleName,
          });
          toast.show({
            action: "success",
            description: "User is invited to organization",
          });
        },
        onError: error => {
          const e = formatError(error);
          toast.show({ action: "danger", description: e.message });
        },
        variables: {
          input: { phoneNumber: fullPhoneNumber, roleId: role?.id ?? "" },
        },
        refetchQueries: [{ query: InvitationsListMySentInvitationsDocument }],
        update: (cache, { data, errors }) => {
          const existingData: InvitationsListMyOrganiationsSentInvitationsQuery | null =
            cache.readQuery({
              query: InvitationsListMyOrganiationsSentInvitationsDocument,
              variables: { status: InvitationStatus.Pending },
            });
          const temporaryData = data?.createInvitation;
          if (!!existingData && !!temporaryData && !errors) {
            cache.writeQuery({
              query: InvitationsListMyOrganiationsSentInvitationsDocument,
              variables: { status: InvitationStatus.Pending },
              data: {
                listMyOrganizationsSentInvitations: {
                  ...existingData.listMyOrganizationsSentInvitations,
                  edges: [
                    ...existingData.listMyOrganizationsSentInvitations.edges,
                    {
                      __typename: "InvitationEdge",
                      cursor: `temp-cursor-${temporaryData.id}`,
                      node: temporaryData,
                    },
                  ],
                },
              },
            });
          }
        },
        optimisticResponse: {
          __typename: "Mutation",
          createInvitation: {
            __typename: "Invitation",
            id: makeOptimisticId("temp-id"),
            phoneNumber: fullPhoneNumber,
            role: role as Role,
            status: InvitationStatus.Pending,
            user: currentUser as User,
            receiver: {
              id: makeOptimisticId("temp-user-id"),
              cognitoUser: {
                Username: "temp-username",
                Attributes: [{ Name: "", Value: "" }],
              },
            },
          },
        },
      })
        .then(() => router.navigate("/invitations"))
        .catch(console.log);
    },
    [createInvitation, currentUser, recordUserGoal, roles, router, toast]
  );

  const doSubmit = handleSubmit(submit);

  const { width } = useWindowDimensions();
  const widthContent = width >= MD_BREAKPOINT ? ifWeb(640, 750) : width;

  useSetPageTitle("Send Invitation");

  return (
    <CreateInvitationView
      onSubmit={doSubmit}
      isValid={isValid}
      widthContent={widthContent}
      control={control}
      errors={errors}
      loading={isSubmitting}
      keyboardVerticalOffset={keyboardVerticalOffset}
    />
  );
};

export default CreateInvitationContainer;
